import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Button, Card, Col, Modal, Row, Spin, Tag, Typography, message, Empty, Image,
} from 'antd';
import {
  ArrowLeftOutlined, SaveOutlined, CameraOutlined, ApartmentOutlined, EditOutlined,
} from '@ant-design/icons';
import GridEditor, { GridData, emptyGrid, parseGridData } from './GridEditor';

const { Title, Text } = Typography;

interface View {
  camera: string;
  trigger_label: string;
  image_url: string;
  matrix_label: string;
  polygon_points: any;
}

type GridMap = Record<string, GridData>;

const viewKey = (v: Pick<View, 'camera' | 'trigger_label'>) =>
  `${v.camera}__${v.trigger_label}`;

const getDiagramSrc = (jobNum: string): string | null => {
  try {
    return require(`../../assets/matrix_diagrams/${jobNum}.png`);
  } catch {
    return null;
  }
};

const MatrixMappingViewsPage: React.FC = () => {
  const { systemName, jobNum } = useParams<{ systemName: string; jobNum: string }>();
  const navigate = useNavigate();

  const [views, setViews] = useState<View[]>([]);
  const [gridMap, setGridMap] = useState<GridMap>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [editingView, setEditingView] = useState<View | null>(null);
  const [modalGrid, setModalGrid] = useState<GridData>(emptyGrid());

  const diagramSrc = jobNum ? getDiagramSrc(jobNum) : null;

  useEffect(() => {
    if (systemName && jobNum) fetchViews();
  }, [systemName, jobNum]);

  const fetchViews = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/matrix-mapping/views?system_name=${encodeURIComponent(systemName!)}&job_num=${encodeURIComponent(jobNum!)}`,
        { credentials: 'include' }
      );
      if (!res.ok) throw new Error('Failed to fetch views');
      const data = await res.json();
      setViews(data.views);
      const initial: GridMap = {};
      for (const v of data.views) {
        initial[viewKey(v)] = parseGridData(v.polygon_points);
      }
      setGridMap(initial);
    } catch {
      message.error('Failed to load views');
    } finally {
      setLoading(false);
    }
  };

  const openEditor = (view: View) => {
    setEditingView(view);
    setModalGrid(gridMap[viewKey(view)] ?? emptyGrid());
  };

  const closeEditor = () => {
    if (editingView) {
      setGridMap(prev => ({ ...prev, [viewKey(editingView)]: modalGrid }));
    }
    setEditingView(null);
    setModalGrid(emptyGrid());
  };

  const handleSave = async () => {
    const toSave = views.map(v => ({
      camera: v.camera,
      trigger_label: v.trigger_label,
      matrix_label: v.matrix_label ?? '',
      polygon_points: gridMap[viewKey(v)] ?? emptyGrid(),
    }));

    setSaving(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/matrix-mapping/save`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ system_name: systemName, job_num: jobNum, labels: toSave }),
      });
      if (!res.ok) throw new Error('Save failed');
      message.success(`Saved ${toSave.length} view${toSave.length !== 1 ? 's' : ''}`);
    } catch {
      message.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const configuredCount = Object.values(gridMap).filter(
    g => g.v_lines.length > 0 || g.h_lines.length > 0
  ).length;

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', padding: 24 }}>

      {/* Header */}
      <Card
        style={{ marginBottom: 24, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: 12 }}
        bodyStyle={{ padding: '16px 24px' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Button icon={<ArrowLeftOutlined />} type='text' onClick={() => navigate('/matrix-mapping')} style={{ paddingLeft: 0 }}>
              Back
            </Button>
            <div style={{ width: 1, height: 28, background: '#e8e8e8' }} />
            <div>
              <Title level={4} style={{ margin: 0, color: '#1890ff' }}>Camera View Mapping</Title>
              <Text type='secondary' style={{ fontSize: 13 }}>
                {systemName} &nbsp;·&nbsp; Job # {jobNum}
              </Text>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {!loading && views.length > 0 && (
              <Tag color='blue'>{configuredCount} / {views.length} views with grid</Tag>
            )}
            <Button
              type='primary'
              icon={<SaveOutlined />}
              loading={saving}
              onClick={handleSave}
              disabled={loading}
              size='large'
              style={{ borderRadius: 8 }}
            >
              Save
            </Button>
          </div>
        </div>
      </Card>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 80 }}>
          <Spin size='large' />
          <div style={{ marginTop: 16 }}><Text type='secondary'>Loading camera views...</Text></div>
        </div>
      ) : views.length === 0 ? (
        <Card style={{ border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: 12 }}>
          <Empty description='No views found for the most recent trigger of this part' style={{ margin: '60px 0' }} />
        </Card>
      ) : (
        <Row gutter={24} align='top'>

          {/* Diagram Panel */}
          {diagramSrc && (
            <Col xs={24} lg={8} xl={7}>
              <div style={{ position: 'sticky', top: 88 }}>
                <Card
                  style={{ border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: 12, overflow: 'hidden' }}
                  bodyStyle={{ padding: 0 }}
                >
                  <div style={{
                    background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                    padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 10,
                  }}>
                    <ApartmentOutlined style={{ color: 'white', fontSize: 18 }} />
                    <div>
                      <Text strong style={{ color: 'white', display: 'block', fontSize: 14 }}>Part Diagram</Text>
                      <Text style={{ color: 'rgba(255,255,255,0.75)', fontSize: 12 }}>Job # {jobNum}</Text>
                    </div>
                  </div>
                  <div style={{ padding: 16 }}>
                    <Image
                      src={diagramSrc}
                      alt={`Diagram for job ${jobNum}`}
                      style={{ width: '100%', borderRadius: 8 }}
                      preview={{ src: diagramSrc }}
                    />
                    <Text type='secondary' style={{ fontSize: 11, display: 'block', textAlign: 'center', marginTop: 8 }}>
                      Click image to zoom
                    </Text>
                  </div>
                </Card>
              </div>
            </Col>
          )}

          {/* Camera View Cards */}
          <Col xs={24} lg={diagramSrc ? 16 : 24} xl={diagramSrc ? 17 : 24}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: 16,
            }}>
              {views.map((view) => {
                const grid = gridMap[viewKey(view)] ?? emptyGrid();
                const hasGrid = grid.v_lines.length > 0 || grid.h_lines.length > 0;
                const labeledCells = Object.values(grid.labels).filter(l => l.trim() !== '').length;
                const totalCells = (grid.v_lines.length + 1) * (grid.h_lines.length + 1);

                return (
                  <div
                    key={viewKey(view)}
                    style={{
                      background: '#fff',
                      borderRadius: 12,
                      overflow: 'hidden',
                      boxShadow: hasGrid ? '0 4px 16px rgba(24,144,255,0.15)' : '0 2px 8px rgba(0,0,0,0.06)',
                      border: hasGrid ? '1px solid #91caff' : '1px solid #f0f0f0',
                      transition: 'box-shadow 0.2s, border-color 0.2s',
                    }}
                  >
                    {/* Thumbnail */}
                    <div style={{ position: 'relative', lineHeight: 0 }}>
                      <img
                        src={view.image_url}
                        alt={`${view.camera} / ${view.trigger_label}`}
                        style={{ width: '100%', height: 160, objectFit: 'cover', display: 'block' }}
                      />
                      {hasGrid && (
                        <div style={{
                          position: 'absolute', top: 8, right: 8,
                          background: 'rgba(24,144,255,0.88)',
                          color: 'white', borderRadius: 10,
                          padding: '2px 8px', fontSize: 11, fontWeight: 600,
                        }}>
                          {grid.v_lines.length}V · {grid.h_lines.length}H
                          {labeledCells > 0 && ` · ${labeledCells}/${totalCells} labeled`}
                        </div>
                      )}
                    </div>

                    <div style={{ padding: '10px 12px 12px' }}>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                        <Tag icon={<CameraOutlined />} color='default' style={{ fontSize: 11, margin: 0 }}>
                          {view.camera}
                        </Tag>
                        <Tag color='geekblue' style={{ fontSize: 11, margin: 0 }}>
                          {view.trigger_label}
                        </Tag>
                      </div>
                      <Button
                        icon={<EditOutlined />}
                        size='small'
                        type={hasGrid ? 'primary' : 'dashed'}
                        style={{ width: '100%' }}
                        onClick={() => openEditor(view)}
                      >
                        {hasGrid ? 'Edit Grid' : 'Set Up Grid'}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </Col>
        </Row>
      )}

      {/* Grid Editor Modal */}
      <Modal
        open={!!editingView}
        onCancel={closeEditor}
        onOk={closeEditor}
        okText='Done'
        cancelButtonProps={{ style: { display: 'none' } }}
        width={diagramSrc ? 1300 : 900}
        title={
          editingView ? (
            <span style={{ fontSize: 15 }}>
              <CameraOutlined style={{ marginRight: 8, color: '#1890ff' }} />
              {editingView.camera}
              <Text type='secondary' style={{ fontWeight: 400, marginLeft: 8, fontSize: 13 }}>
                · {editingView.trigger_label}
              </Text>
            </span>
          ) : null
        }
        destroyOnClose
      >
        {editingView && (
          <Row gutter={20} align='top' wrap={false}>
            {/* Editor */}
            <Col flex='1 1 0' style={{ minWidth: 0 }}>
              <GridEditor
                imageUrl={editingView.image_url}
                grid={modalGrid}
                onChange={setModalGrid}
              />
            </Col>

            {/* Reference diagram */}
            {diagramSrc && (
              <Col flex='0 0 340px'>
                <div style={{ position: 'sticky', top: 0 }}>
                  <div style={{
                    background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                    padding: '10px 14px',
                    borderRadius: '8px 8px 0 0',
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}>
                    <ApartmentOutlined style={{ color: 'white', fontSize: 15 }} />
                    <div>
                      <Text strong style={{ color: 'white', display: 'block', fontSize: 13 }}>Reference Diagram</Text>
                      <Text style={{ color: 'rgba(255,255,255,0.75)', fontSize: 11 }}>Job # {jobNum}</Text>
                    </div>
                  </div>
                  <div style={{
                    border: '1px solid #d9d9d9',
                    borderTop: 'none',
                    borderRadius: '0 0 8px 8px',
                    overflow: 'hidden',
                    background: '#fafafa',
                  }}>
                    <Image
                      src={diagramSrc}
                      alt={`Diagram for job ${jobNum}`}
                      style={{ width: '100%', display: 'block' }}
                      preview={{ src: diagramSrc }}
                    />
                    <Text type='secondary' style={{ fontSize: 11, display: 'block', textAlign: 'center', padding: '6px 0' }}>
                      Click to zoom
                    </Text>
                  </div>
                </div>
              </Col>
            )}
          </Row>
        )}
      </Modal>
    </div>
  );
};

export default MatrixMappingViewsPage;
